import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, PackageCheck } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { ProductImageEditor } from "@/components/admin/ProductImageEditor";
import { SecureImage } from "@/components/SecureImage";
import { api, formatApiErrorDetail } from "@/lib/api";

const money = (value) => `₹${Number(value || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

export default function AdminProductDetailPage() {
  const { productId } = useParams();
  const [product, setProduct] = useState(null);
  const [error, setError] = useState("");

  const loadProduct = useCallback(async () => {
    try {
      const response = await api.get(`/admin/products/${productId}`);
      setProduct(response.data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Product not found");
    }
  }, [productId]);

  useEffect(() => {
    loadProduct();
  }, [loadProduct]);

  if (error) {
    return <div className="flex min-h-screen items-center justify-center bg-[#0B0F17] text-red-300" data-testid="admin-product-details-error">{error}</div>;
  }
  if (!product) {
    return <div className="flex min-h-screen items-center justify-center bg-[#0B0F17] text-slate-400" data-testid="admin-product-details-loading">Loading product details...</div>;
  }

  const image = product.images?.[0];
  return (
    <div className="min-h-screen bg-[#0B0F17] text-slate-100" data-testid="admin-product-details-page">
      <main className="mx-auto max-w-6xl px-4 py-8">
        <Link to="/admin" data-testid="admin-product-detail-back-link" className="mb-6 inline-flex items-center gap-2 text-sm text-blue-300 transition-colors hover:text-blue-200">
          <ArrowLeft className="h-4 w-4" /> Back to review dashboard
        </Link>
        <div className="grid gap-8 lg:grid-cols-[420px_1fr]">
          <section className="overflow-hidden rounded-3xl border border-slate-800 bg-slate-900/80">
            {image?.file_id ? (
              <SecureImage fileId={image.file_id} alt={product.name} className="h-96 w-full object-contain bg-slate-950" />
            ) : (
              <div className="flex h-96 flex-col items-center justify-center text-slate-500">
                <PackageCheck className="mb-3 h-12 w-12" /> No product image
              </div>
            )}
            <div className="p-5">
              <ProductImageEditor product={product} onUpdated={setProduct} className="w-full" />
            </div>
          </section>
          <section className="rounded-3xl border border-slate-800 bg-slate-900/80 p-6">
            <div className="mb-4 flex flex-wrap items-center gap-2">
              <Badge className="border-blue-500/30 bg-blue-500/10 text-blue-300">{product.status}</Badge>
              <Badge className="border-slate-700 bg-slate-800 text-slate-200">MOQ {product.moq} pcs</Badge>
              {product.quantity_filtered ? <Badge className="border-amber-500/30 bg-amber-500/10 text-amber-300">Quantity filtered</Badge> : null}
            </div>
            <h1 className="font-headline text-4xl font-bold text-white">{product.name}</h1>
            <p className="mt-4 leading-7 text-slate-400">{product.description}</p>
            <div className="mt-8 grid gap-4 sm:grid-cols-2">
              <Detail label="Public SKU" value={product.public_sku} mono />
              <Detail label="HSN" value={product.hsn_code || "-"} />
              <Detail label="Colors" value={(product.colors || []).join(", ") || "-"} />
              <Detail label="GST" value={`${product.gst_rate || 0}%`} />
              <Detail label="Minimum quantity" value={product.minimum_quantity || 1} />
              <Detail label="Retail price" value={money(product.retail_price)} />
              <Detail label="Transport" value={money(product.transport_cost)} />
            </div>
          </section>
        </div>
      </main>
    </div>
  );
}

function Detail({ label, value, mono = false }) {
  return (
    <div className="rounded-2xl border border-slate-800 bg-slate-950/60 p-4">
      <div className="text-xs uppercase tracking-[0.2em] text-slate-500">{label}</div>
      <div className={`mt-2 text-sm font-semibold text-slate-100 ${mono ? "font-mono" : ""}`}>{value}</div>
    </div>
  );
}
